import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Dialog,
  DialogContent,
  IconButton,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  Stack,
  CircularProgress,
  Button,
  InputAdornment,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import axios from "axios";
import { useUniboxColors } from "../../useUniboxColors";

const API = process.env.REACT_APP_API_URL;

const TYPE_FILTERS = [
  { id: "all", label: "Todos" },
  { id: "pdf", label: "PDF" },
  { id: "image", label: "Imágenes" },
  { id: "excel", label: "Excel" },
  { id: "word", label: "Word" },
  { id: "other", label: "Otros" },
];

function formatBytes(bytes) {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(ts) {
  if (!ts) return "";
  const d = new Date(Number(ts));
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("es-ES", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function fileIcon(filename, mimetype) {
  const name = String(filename || "").toLowerCase();
  const mime = String(mimetype || "").toLowerCase();
  if (mime.includes("pdf") || name.endsWith(".pdf")) {
    return <PictureAsPdfOutlinedIcon sx={{ fontSize: 22, color: "#dc2626" }} />;
  }
  if (mime.startsWith("image/")) {
    return <ImageOutlinedIcon sx={{ fontSize: 22, color: "#7c3aed" }} />;
  }
  if (
    name.match(/\.xls[xm]?$/) ||
    mime.includes("spreadsheet") ||
    mime.includes("excel")
  ) {
    return <TableChartOutlinedIcon sx={{ fontSize: 22, color: "#16a34a" }} />;
  }
  if (name.match(/\.docx?$/) || mime.includes("word") || mime.includes("msword")) {
    return <DescriptionOutlinedIcon sx={{ fontSize: 22, color: "#2563eb" }} />;
  }
  return <InsertDriveFileOutlinedIcon sx={{ fontSize: 22, color: "#64748b" }} />;
}

function authHeaders() {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function AttachmentsLibrary({
  open,
  onClose,
  accounts = [],
  folder = "inbox",
  onOpenInConversation,
}) {
  const c = useUniboxColors();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [accountFilter, setAccountFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    if (!open) return;
    setPage(1);
  }, [open, debouncedSearch, accountFilter, typeFilter, folder]);

  const loadPage = useCallback(
    async (pageNum, append = false) => {
      if (!API) return;
      if (append) setLoadingMore(true);
      else setLoading(true);

      try {
        const res = await axios.get(`${API}/emails/attachments/browse`, {
          params: {
            q: debouncedSearch || undefined,
            account: accountFilter || undefined,
            type: typeFilter === "all" ? undefined : typeFilter,
            folder: folder === "trash" ? "trash" : "inbox",
            page: pageNum,
            limit: 50,
          },
        });

        const nextItems = res.data?.items || [];
        setTotal(Number(res.data?.total || 0));
        setItems((prev) => (append ? [...prev, ...nextItems] : nextItems));
        setPage(pageNum);
      } catch (err) {
        console.error("Error cargando adjuntos:", err);
        if (!append) {
          setItems([]);
          setTotal(0);
        }
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [debouncedSearch, accountFilter, typeFilter, folder]
  );

  useEffect(() => {
    if (!open) return;
    loadPage(1, false);
  }, [open, loadPage]);

  const hasMore = items.length < total;

  const accountOptions = useMemo(
    () =>
      accounts.map((a) => ({
        email: String(a.email || "").toLowerCase(),
        label: a.email,
      })),
    [accounts]
  );

  const handleDownload = async (item, event) => {
    event?.stopPropagation?.();
    try {
      const res = await fetch(`${API}/emails/attachment/${item.id}/download`, {
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error("download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = item.filename || "adjunto";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      window.alert("No se pudo descargar el archivo.");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: c.radiusOuter,
          bgcolor: c.card,
          border: `1px solid ${c.border}`,
          boxShadow: c.shadowMd,
          overflow: "hidden",
          maxHeight: "92vh",
        },
      }}
    >
      <Box
        sx={{
          px: 2.5,
          py: 2,
          borderBottom: `1px solid ${c.border}`,
          background: `linear-gradient(135deg, ${c.accentSoft} 0%, ${c.card} 55%)`,
          display: "flex",
          alignItems: "flex-start",
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: c.radius,
            bgcolor: c.accentSoft,
            border: `1px solid ${c.accentBorder}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <FolderOpenOutlinedIcon sx={{ color: c.accent }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 800, fontSize: 18, letterSpacing: -0.3 }}>
            Archivos adjuntos
          </Typography>
          <Typography sx={{ fontSize: 12, color: c.textMuted, mt: 0.25 }}>
            Busca y descarga archivos de todos tus correos, filtrados por cuenta.
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ mt: -0.25 }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ px: 2.5, py: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Buscar por nombre, asunto o remitente…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 20, color: c.textDim }} />
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: c.radius,
              bgcolor: c.panel,
            },
          }}
        />

        <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
          <Chip
            label="Todas las cuentas"
            size="small"
            onClick={() => setAccountFilter("")}
            sx={{
              fontWeight: 700,
              bgcolor: !accountFilter ? c.accent : c.panel,
              color: !accountFilter ? "#fff" : c.textPrimary,
              border: `1px solid ${!accountFilter ? c.accent : c.border}`,
            }}
          />
          {accountOptions.map(({ email, label }) => (
            <Chip
              key={email}
              label={label}
              size="small"
              onClick={() => setAccountFilter(email)}
              sx={{
                fontWeight: 600,
                maxWidth: 220,
                bgcolor: accountFilter === email ? c.accent : c.panel,
                color: accountFilter === email ? "#fff" : c.textPrimary,
                border: `1px solid ${accountFilter === email ? c.accent : c.border}`,
                "& .MuiChip-label": {
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                },
              }}
            />
          ))}
        </Box>

        <ToggleButtonGroup
          size="small"
          exclusive
          value={typeFilter}
          onChange={(_, v) => v && setTypeFilter(v)}
          sx={{
            flexWrap: "wrap",
            gap: 0.5,
            "& .MuiToggleButtonGroup-grouped": {
              borderRadius: `${c.radius} !important`,
              border: `1px solid ${c.border} !important`,
              mx: 0,
              fontSize: 11,
              fontWeight: 700,
              textTransform: "none",
              px: 1.25,
              py: 0.35,
            },
            "& .Mui-selected": {
              bgcolor: `${c.filterSelectedBg} !important`,
              color: `${c.filterSelectedColor} !important`,
            },
          }}
        >
          {TYPE_FILTERS.map((t) => (
            <ToggleButton key={t.id} value={t.id}>
              {t.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <Typography sx={{ fontSize: 11, color: c.textDim, fontWeight: 600 }}>
          {loading ? "Cargando…" : `${total} archivo${total === 1 ? "" : "s"}`}
        </Typography>

        <Box
          sx={{
            flex: 1,
            minHeight: 280,
            maxHeight: "52vh",
            overflowY: "auto",
            borderRadius: c.radiusOuter,
            border: `1px solid ${c.border}`,
            bgcolor: c.panel,
          }}
        >
          {loading && items.length === 0 ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress size={28} sx={{ color: c.accent }} />
            </Box>
          ) : items.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 6, px: 2 }}>
              <Typography sx={{ fontWeight: 700, color: c.textPrimary }}>
                No hay archivos
              </Typography>
              <Typography sx={{ fontSize: 12, color: c.textMuted, mt: 0.5 }}>
                Prueba otro filtro o sincroniza tus cuentas.
              </Typography>
            </Box>
          ) : (
            <Stack divider={<Box sx={{ borderBottom: `1px solid ${c.border}` }} />}>
              {items.map((item) => (
                <Box
                  key={item.id}
                  sx={{
                    px: 1.5,
                    py: 1.15,
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.25,
                    cursor: onOpenInConversation ? "pointer" : "default",
                    "&:hover": { bgcolor: c.cardHover },
                  }}
                  onClick={() => onOpenInConversation?.(item)}
                >
                  <Box sx={{ pt: 0.35, flexShrink: 0 }}>
                    {fileIcon(item.filename, item.mimetype)}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: 13,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {item.filename}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 11,
                        color: c.textMuted,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {item.subject || "(Sin asunto)"}
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={0.75}
                      sx={{ mt: 0.45, flexWrap: "wrap", gap: 0.5 }}
                    >
                      <Chip
                        label={item.accountEmail}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: 10,
                          fontWeight: 600,
                          bgcolor: c.metaBg,
                          border: `1px solid ${c.border}`,
                        }}
                      />
                      <Typography sx={{ fontSize: 10, color: c.textDim, alignSelf: "center" }}>
                        {formatBytes(item.size)} · {formatDate(item.internalDate)}
                      </Typography>
                    </Stack>
                  </Box>
                  <Stack direction="row" spacing={0.25} sx={{ flexShrink: 0 }}>
                    <IconButton
                      size="small"
                      title="Descargar"
                      onClick={(e) => handleDownload(item, e)}
                    >
                      <DownloadOutlinedIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                    {onOpenInConversation ? (
                      <IconButton
                        size="small"
                        title="Abrir conversación"
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpenInConversation(item);
                        }}
                      >
                        <OpenInNewOutlinedIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    ) : null}
                  </Stack>
                </Box>
              ))}
            </Stack>
          )}
        </Box>

        {hasMore && !loading ? (
          <Button
            fullWidth
            variant="outlined"
            disabled={loadingMore}
            onClick={() => loadPage(page + 1, true)}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: c.radius,
              borderColor: c.border,
            }}
          >
            {loadingMore ? "Cargando…" : `Cargar más (${items.length} de ${total})`}
          </Button>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
